import { HttpClient } from 'api/http-client';

export enum CustomerPortalInviteMethod {
  EmailsFromCustomerDomainsAndAdditionalDomains = 'EMAILS_FROM_CUSTOMER_DOMAINS_AND_ADDITIONAL_DOMAINS',
  AnyoneWithTheLink = 'ANYONE_WITH_THE_LINK',
}

export interface CustomerPortalInviteMethodMeta {
  userWithPermissionCanInviteCustomers?: boolean;
  customerCanBeAddedViaJWT?: boolean;
}

export enum AccountCustomerPortalType {
  Old = 'OLD',
  New = 'NEW',
}

export interface InviteSettingsPermissionConfig {
  canDeletePhases: boolean;
  canViewTaskDesc: boolean;
  canEditTaskDesc: boolean;
  canEditTaskName: boolean;
  canDeleteTasks: boolean;
  canDeleteSpaceTabs: boolean;
}

export interface AccountCustomerPortalSettings {
  publicKey?: string | null;
  sharedSecret?: string | null;
  algorithm: CustomerPortalSettingsAlgorithm;
  mechanism: CustomerPortalSettingsMechanism;
  createdAt?: number;
  updatedAt?: number;
  createdBy?: number;
  updatedBy?: number;
  accountCustomerPortalSettingsId?: number;
  inviteMethod: CustomerPortalInviteMethod;
  inviteMethodMeta: CustomerPortalInviteMethodMeta;
  portalType: AccountCustomerPortalType;
  defaultPortalId?: number;
  emailRedirectUrl?: string;
  inviteSettingsPermissionConfig?: InviteSettingsPermissionConfig;
  codeBlockUrl?: string;
}

export type EmbedSettings = Omit<
  AccountCustomerPortalSettings,
  'inviteMethod' | 'inviteMethodMeta' | 'inviteSettingsPermissionConfig'
>;

export type InviteSettings = Omit<
  AccountCustomerPortalSettings,
  'publicKey' | 'sharedSecret' | 'algorithm' | 'mechanism'
>;

export type InviteSettingsUpdatePayload = InviteSettings & {
  codeBlock?: string;
};

export interface CustomerPortalSettingsRequestAndResponse {
  customerPortalSettings: AccountCustomerPortalSettings;
}

export enum CustomerPortalSettingsAlgorithm {
  RS256 = 'RS256',
  HS256 = 'HS256',
}

export enum CustomerPortalSettingsMechanism {
  SharedSecret = 'SHARED_SECRET',
  PublicPrivateKeyPair = 'PUBLIC_PRIVATE_KEY_PAIR',
}

export interface CustomerPortalSecretKeyGenerationRequest {
  algorithm: CustomerPortalSettingsAlgorithm;
}

export interface CustomerPortalSecretKeyGenerationResponse {
  secretKey: string;
}

export interface CodeBlockResponse {
  codeBlock: string | null;
}

class AccountCustomerPortalSettingsHttpClient extends HttpClient {
  public getEmbedSettings() {
    return this.get<EmbedSettings>('/settings/customer-portal/embed-settings');
  }

  public getInviteSettings() {
    return this.get<InviteSettings>(
      '/settings/customer-portal/invite-settings',
    );
  }

  public updateEmbedSettings(payload: EmbedSettings): Promise<EmbedSettings> {
    return this.post('/settings/customer-portal/embed-settings', payload);
  }

  public updateInviteSettings(
    payload: InviteSettingsUpdatePayload,
  ): Promise<InviteSettings> {
    return this.post('/settings/customer-portal/invite-settings', payload);
  }

  public switchToNew(): Promise<CustomerPortalSettingsRequestAndResponse> {
    return this.post('/settings/customer-portal/switch-to-new');
  }

  public generateSecretKey(
    payload: CustomerPortalSecretKeyGenerationRequest,
  ): Promise<CustomerPortalSecretKeyGenerationResponse> {
    return this.post('/settings/customer-portal/generate-secret', payload);
  }

  public isRedirectEnabled(): Promise<{
    enabled: boolean;
  }> {
    return this.get('/settings/customer-portal/redirect/enable');
  }

  public getCodeBlock(): Promise<CodeBlockResponse> {
    return this.get<CodeBlockResponse>('/settings/customer-portal/code-block');
  }

  public isRTEEnabledInCpMultiLineText(): Promise<{
    enabled: boolean;
  }> {
    return this.get('/settings/customer-portal/rich-text/enable');
  }
}

export const CustomerPortalSettingsApi =
  new AccountCustomerPortalSettingsHttpClient();
